import { DocsCallout, DocsContent, DocsLayout, H2, H3 } from '@/components/docs'

export default function DocsTimelineView() {
  return (
    <DocsLayout>
      <DocsContent
        title="Timeline View"
        description="Analyse a case over time — the Timeline View combines the knowledge graph, the geolocation map, and a scrubbable event timeline into one synchronised workspace."
        previousPage={{ title: 'Investigation Analysis', href: '/docs/investigation-analysis' }}
        nextPage={{ title: 'Natural Language Queries', href: '/docs/natural-language' }}
      >
        <H2 id="layout">Layout</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Timeline view is a three-pane workspace. Everything you see is driven by the same time
          state — pick an entity in one pane and it is highlighted everywhere, scrub the playhead
          and every pane updates in lock-step.
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Left pane — Knowledge Graph.</strong> The same
              graph that powers the Graph Explorer, but driven by the current time-range filter and
              playhead. Edges whose timestamp falls outside the active window are hidden.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Right pane — tabbed.</strong> Switch between the{' '}
              <strong className="text-foreground">Map</strong> tab (a 3D Cesium globe with entity
              markers and animated tracks) and the{' '}
              <strong className="text-foreground">Events</strong> tab (a sortable, searchable list
              of temporal events).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Bottom strip — Unified Timeline.</strong> The
              scrubber, ruler, playhead, range handles, playback controls, and minimap live here.
              The strip hides itself when the case has no temporal data at all.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Drag divider</strong> between the left and right
              panes — grab it and drag to adjust the split between 20% and 80%.
            </span>
          </li>
        </ul>

        <H2 id="temporal-events">Temporal Events</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Temporal events are extracted from graph{' '}
          <strong className="text-foreground">edges</strong>, not nodes. An edge becomes an event if
          its properties contain any of:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <code className="text-xs bg-muted px-1 py-0.5 rounded">timestamp</code>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <code className="text-xs bg-muted px-1 py-0.5 rounded">occurred_at</code>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <code className="text-xs bg-muted px-1 py-0.5 rounded">posted_at</code>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          The first property found on the edge is used as the event timestamp. Events inherit the
          edge type (<code className="text-xs bg-muted px-1 py-0.5 rounded">CONTACTED</code>,{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">POSTED_FROM</code>,{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">LOCATED_AT</code>,{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">TRACKED_IN</code>, and so on) and
          are sorted chronologically.
        </p>

        <H3 id="event-colours">Edge-Type Colour Coding</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The Events tab colours each row by its edge type so you can scan a long timeline at a
          glance:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-1">●</span>
            <span>
              <code className="text-xs bg-muted px-1 py-0.5 rounded">CONTACTED</code> — blue (phone
              calls, messages, direct contact events)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-1">●</span>
            <span>
              <code className="text-xs bg-muted px-1 py-0.5 rounded">POSTED_FROM</code> — green
              (social-media posts and other authored content)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">●</span>
            <span>
              <code className="text-xs bg-muted px-1 py-0.5 rounded">LOCATED_AT</code> — amber
              (entity-at-location events)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-500 mt-1">●</span>
            <span>
              <code className="text-xs bg-muted px-1 py-0.5 rounded">TRACKED_IN</code> — purple
              (tracking and surveillance events)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-muted-foreground mt-1">●</span>
            <span>Any other edge type renders in a neutral tone.</span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          Clicking an event row focuses the source node in the knowledge graph. When the source
          entity has a geo mapping, the map also flies to that entity, so a single click on the
          events list re-centres every pane on the same subject.
        </p>

        <H2 id="playhead">Playhead Cursor</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The <strong className="text-foreground">playhead</strong> is a vertical cyan cursor on the
          timeline ruler representing "current time". Internally it is stored as an ISO-8601 string
          in the timeline store, which is shared with the graph and the Cesium viewer.
        </p>

        <H3 id="playhead-controls">Moving the Playhead</H3>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Click or drag on the ruler</strong> to jump the
              playhead to a specific time.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Play / Pause</strong> — animates the playhead
              forward in real time. The Cesium clock is synchronised to it, so map tracks animate in
              lock-step with the scrubber.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Skip to start / Skip to end</strong> — jumps the
              playhead to the current filter boundaries (not the absolute edges of the dataset).
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Step back / Step forward</strong> — nudges the
              playhead by 1% of the current filter window per click.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Playback speed</strong> —{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">1×</code>,{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">2×</code>,{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">5×</code>, and{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">10×</code> via the speed
              selector. These values also drive Cesium's{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">clock.multiplier</code>, so the
              map animation speeds up accordingly.
            </span>
          </li>
        </ul>

        <H3 id="playhead-effects">What the Playhead Affects</H3>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Graph edges</strong> — edges with a timestamp
              later than the playhead are hidden. The effective time filter becomes{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">
                [filterStart, min(filterEnd, playheadTime)]
              </code>
              .
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Map entities</strong> — the Cesium viewer's clock
              is kept in sync with the playhead, so sampled-position tracks animate forward in
              lock-step with the scrubber.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Events tab</strong> — events later than the
              playhead are hidden by the same clamp.
            </span>
          </li>
        </ul>

        <p className="text-foreground mb-4 leading-relaxed">
          Semantically the playhead means{' '}
          <em>"show the state of the case up to and including time T"</em>. The view is a moving
          cumulative snapshot, not an instantaneous highlight — edges before T stay visible, edges
          after T disappear.
        </p>

        <DocsCallout variant="tip" title="Scrub Within a Range">
          You can set a wide time range and then scrub the playhead through it to watch the
          investigation evolve — the range handles define the outer bounds, and the playhead slides
          through that window. Pressing Play then gives you a replay of exactly that slice.
        </DocsCallout>

        <H2 id="time-range-filter">Time-Range Filter</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The <strong className="text-foreground">time-range filter</strong> is defined by two
          draggable cyan handles on the ruler:{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">filterStart</code> and{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">filterEnd</code>. On first load the
          range is initialised to the full span of temporal data available in the case.
        </p>

        <H3 id="range-controls">Setting the Range</H3>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Drag the handles</strong> — the left handle moves{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">filterStart</code>, the right
              handle moves <code className="text-xs bg-muted px-1 py-0.5 rounded">filterEnd</code>.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Scroll to zoom</strong> — the mouse wheel zooms
              the ruler in and out, anchored at the cursor position. Dedicated zoom buttons are
              available too. The minimum zoom window is 60 seconds.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Minimap</strong> — a strip at the bottom of the
              ruler shows the current view window relative to the full dataset. Click anywhere on
              the minimap to pan the ruler to that position.
            </span>
          </li>
        </ul>

        <H3 id="range-effects">What the Range Filter Affects</H3>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Graph edges</strong> — edges with timestamps
              outside{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">[filterStart, filterEnd]</code>{' '}
              are removed from the graph, combined with the playhead clamp described above.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Events tab</strong> — only events inside the
              window are listed.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Map entities</strong> — entities whose entire
              track falls outside the window are hidden. An entity with at least one sample inside
              the window is retained, so partial overlaps still show up on the globe.
            </span>
          </li>
        </ul>

        <DocsCallout variant="info" title="Playhead Interacts With the Range">
          The effective "up to" cutoff used across every pane is{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">min(filterEnd, playheadTime)</code>
          . This means you can pre-load a range and then scrub within it without ever losing the
          outer bound — the range is a hard ceiling, the playhead slides underneath it.
        </DocsCallout>

        <H2 id="empty-states">Empty States</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          The Timeline view communicates three distinct "nothing to show" conditions so you can tell
          them apart at a glance:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">No temporal data at all</strong> — the Events tab
              shows <em>"No temporal events found in graph"</em> and the bottom scrubber hides
              itself entirely. If you see this, check whether your extraction schema exposes{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">timestamp</code>,{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">occurred_at</code>, or{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">posted_at</code> on the
              relevant relation types. Without any of those three properties on any edge, the
              Timeline view has nothing to plot.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Filter collapses the window to zero</strong> — the
              Events tab shows <em>"No events match the current filters"</em>. The dataset is fine;
              the range handles or an active pattern filter have excluded everything.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">No geo data matches the pattern filter</strong> —
              the Map tab shows an overlay that reads{' '}
              <em>"No location data matches the current filter"</em>. Other panes may still have
              content.
            </span>
          </li>
        </ul>

        <H2 id="cross-view-selection">Cross-View Selection</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Selection is shared across all three panes. Clicking an entity on the{' '}
          <strong className="text-foreground">map</strong>, in the{' '}
          <strong className="text-foreground">graph</strong>, or on a row in the{' '}
          <strong className="text-foreground">events list</strong> selects that entity everywhere —
          the graph centres on the corresponding node, the map flies to the entity, and the events
          list highlights its rows.
        </p>

        <p className="text-foreground mb-4 leading-relaxed">
          Clicking an empty area in any pane clears the selection across all three, so you never end
          up with a stale highlight stuck on a pane you're not looking at.
        </p>
      </DocsContent>
    </DocsLayout>
  )
}
